/*
 * Copyright (c) 2023 Trimble Inc. All rights reserved.
 */

import { loadFromLocalStorage, saveToLocalStorage, checkLocalStorageExpiry } from './localStorage';
import { asApplicationJsonBody } from './graphqlJsonBody';
import { fetchModulesEndPoint, fetchServiceLevelsEndpoint, fetchFamiliesEndPoint, serviceManualEndPoint, firmTypeEndPoint } from './constants';
import type { UserRole } from './UserModel';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import 'jspdf-autotable';
import { HubConnectionBuilder, HubConnection } from '@microsoft/signalr';
import { AuthService } from '../services/AuthServices';

let serviceManualTypes: any = undefined;
let productFamilies: any = undefined;
let helpButtons: any = undefined;
let firmTypes: any = undefined;

/** Azure WAF CRS treats '=', '+', and '/' in JSON values as SQLi. Use unpadded base64url. */
export function toWafSafeBase64(value: string): string {
    return btoa(unescape(encodeURIComponent(value)))
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=+$/, '');
}

export function appendWafSafeFormField(form: FormData, name: string, value: string, encodingFieldName: string): void {
    form.append(name, toWafSafeBase64(value == null ? '' : value));
    form.append(encodingFieldName, 'base64');
}

/**
 * Multipart file part: keep a filename header (Azure WAF treats that as a file upload,
 * not the 128KB request-body limit) but never send .pdf or application/pdf on the wire.
 */
export function appendWafSafeFile(form: FormData, name: string, file: any, originalFileName: string): void {
    if (file == null || typeof file === 'string' || !(file instanceof Blob)) {
        return;
    }
    const payload = file.slice(0, file.size, 'application/octet-stream');
    form.append(name, payload, toWafSafeBase64(originalFileName == null ? 'file' : `${originalFileName}`));
}

/** Azure WAF treats backticks / hyphens in FileName as SQL (942xxx). Send unpadded base64url instead. */
export function toFileNameLookupBody(fileName: string): string {
    return JSON.stringify({
        FileName: toWafSafeBase64(fileName),
        FileNameEncoding: 'base64'
    });
}

/** Azure WAF treats https:// S3 URLs in JSON as SSRF/SQLi. Send unpadded base64url instead. */
export function toUrlLookupBody(url: string): string {
    return JSON.stringify({
        URL: toWafSafeBase64(url),
        URLEncoding: 'base64'
    });
}

export function getDateDiff(date1: Date, date2: Date): number {
    const diffTime = date2.getTime() - date1.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
}
export function isAdministrator() {
    return isRoleExists(2);
}

export function isRoleExists(roleId: number) {
    const myrole: any = loadFromLocalStorage('MyRole');
    if (myrole == null || myrole === undefined || myrole.find((role: any) => {
        return role.roleid === roleId;
    }) === undefined) {
        return false;
    }
    return true;

}

export const getAllModules = async () => {
    const token: string = loadFromLocalStorage('AuthToken');
    const encodedToken = 'Bearer ' + token;
    const headerDict: any = {
        'Authorization': encodedToken,
        'Content-Type': 'application/json'
    };
    const queryString = `query{
	                  allmodules 
	                  {
		                  count
		                  data{
			
				                  modulename
				                  id
				                  }
			
	                  }
                  }`

    try {
        if (navigator.onLine) {
            const response = await fetch(fetchModulesEndPoint, {
                headers: new Headers(headerDict),
                body: asApplicationJsonBody(queryString),
                method: 'POST'
            });
            const responseData = await response.json();
            const allModules: any = responseData.data.allmodules.data;
            saveToLocalStorage(allModules, 'All Modules');
            return allModules;
        }
    }
    catch (e) {
        console.log(e);
    }
}

export const getAllRoles = async () => {
    const token: string = loadFromLocalStorage('AuthToken');
    const encodedToken = 'Bearer ' + token;
    const headerDict: any = {
        'Authorization': encodedToken,
        'Content-Type': 'application/json'
    };
    const queryString = `query{	
        allroles
	    {
		    data{
			    rolename
			    roleid
                isInternalRole
		    }
	    }
    }`

    try {
        if (navigator.onLine) {
            const response = await fetch(fetchModulesEndPoint, {
                headers: new Headers(headerDict),
                body: asApplicationJsonBody(queryString),
                method: 'POST'
            });
            const responseData = await response.json();
            const allRoles: Array<UserRole> = responseData.data.allroles.data;
            saveToLocalStorage(allRoles, 'AllRoles');
            return allRoles;
        }
    }
    catch (e) {
        console.log(e);
    }
}
export const getAllServiceLevels = async () => {
    const token: string = loadFromLocalStorage('AuthToken');
    const encodedToken = 'Bearer ' + token;
    const headerDict: any = {
        'Authorization': encodedToken,
        'Content-Type': 'application/json'
    };
    const queryString = `query{
	allServiceLevels{
		count,
		data{
			serviceLevelId,
			serviceLevelValue
		}
	}
}`

    try {
        if (navigator.onLine) {
            const response = await fetch(fetchServiceLevelsEndpoint, {
                headers: new Headers(headerDict),
                body: asApplicationJsonBody(queryString),
                method: 'POST'
            });
            const responseData = await response.json();
            const allServiceLevels: any = responseData.data.allServiceLevels.data;
            saveToLocalStorage(allServiceLevels, 'All ServiceLevels');
            return allServiceLevels;
        }
    }
    catch (e) {
        console.log(e);
    }
}

export async function sendRequestAndReceiveResponse(body: any, endPoint: any, method: any = 'POST') {
    const token: string = loadFromLocalStorage('AuthToken');
    const encodedToken = 'Bearer ' + token;
    const headerDict: any = {
        'Authorization': encodedToken,
        'Content-Type': 'application/json'
    };

    console.log('sendRequestAndReceiveResponse - endpoint:', endPoint, 'method:', method, 'body:', body);

    try {
        if (navigator.onLine) {
            const response = await fetch(endPoint, {
                headers: new Headers(headerDict),
                body: asApplicationJsonBody(body),
                method

            });
            const result = await response.json();
            console.log('sendRequestAndReceiveResponse - response:', result);
            return result;
        }
    }
    catch (e) {
        console.error('sendRequestAndReceiveResponse - error:', e);
    }
}

export function exportData(selectedColumns: any, rowData: any, exportType: any, fileName: any, columnHeaders: any, mode: any) {
    const columns: any = [];
    const rows: any[] = [];
    selectedColumns.map((column: any) => {
        if (column.display === true)
            columns.push({ title: column.title, dataKey: column.title });

    });
    rowData !== undefined && rowData.map((row: any) => {
        const temp: any = {};
        selectedColumns.map((column: any) => {
            if (column.display === true) {
                temp[column.title] = row[column.name];
            }
        });
        rows.push(temp);
    });
    switch (exportType) {
        case 0:
            exportCsv(rows, columns.map((value: any) => {
                return value.title;
            }), fileName);
            break;
        case 1:
            exportPdf(rows, columns, columnHeaders, fileName, mode);
            break;
    }
}

export async function exportCsv(rows: any[], columns: any[], fileName: string) {
    const escapeCell = (cell: any) => {
        const s = cell == null ? '' : String(cell);
        const needsQuotes = /[",\n]/.test(s);
        const escaped = s.replace(/"/g, '""');
        return needsQuotes ? `"${escaped}"` : escaped;
    };
    const header = columns.map(escapeCell).join(',');
    const body = rows.map(row => columns.map((col: any) => escapeCell(row[col])).join(',')).join('\n');
    const csv = `${header}\n${body}`;
    const hiddenElement = document.createElement('a');
    const csvData = new Blob([csv], { type: 'text/csv' });
    const csvUrl = URL.createObjectURL(csvData);
    hiddenElement.href = csvUrl;
    hiddenElement.target = '_blank';
    hiddenElement.download = `${fileName}.csv`;
    hiddenElement.click();
}
export function exportPdf(rows: any, columns: any, columnHeaders: Object, fileName: string, mode: string) {

    /*
   
     An Open issue in the JSPDF - AUTOTABLE
   
     https://github.com/simonbengtsson/jsPDF-AutoTable/issues/469
   
     Row Content is NOT properly wrapped to corresponding cells in last rows (on few pages) and cutoff on next page
   
    */

    const doc = new (jsPDF as any)(mode, 'pt', 'a3');
    autoTable(doc as any, {
        columns,
        body: rows,
        //  html: "#gridTable"
        theme: 'grid',
        styles: {
            halign: 'center',
            overflow: 'linebreak',

        },
        headStyles: {
            fillColor: [33, 150, 243],
            halign: 'center'
        },
        margin: {
            top: 100
        },
        columnStyles: columnHeaders as any
    });

    doc.save(`${fileName}.pdf`);
}

export function checkUserAuthorized() {

    const authToken = loadFromLocalStorage('AuthToken');
    let enabledModules: Array<Object> = loadFromLocalStorage('Enabled Modules');
    const tokenExpiry = loadFromLocalStorage('ExpiryTime');
    const refreshToken = loadFromLocalStorage('RefreshToken');

    if (authToken === null || authToken === undefined || enabledModules === null || enabledModules === undefined
        || tokenExpiry === null || tokenExpiry === undefined || refreshToken === null || refreshToken === undefined) {
        try {
            const base = (import.meta.env.BASE_URL || '/');
            const normalizedBase = base.endsWith('/') ? base : base + '/';
            window.location.assign(normalizedBase + 'signin');

        } catch (e) {
            console.log(e);
        }
    }
    else {
        if (new Date() > tokenExpiry && (!loadFromLocalStorage('Client Mode') || loadFromLocalStorage('Client Mode') !== 'Desktop')) {
                //Expired.
                try {
                    new AuthService().RefreshToken(refreshToken);
                    enabledModules = loadFromLocalStorage('Enabled Modules');
                }
                catch (e) {
                    throw e;
                }
            }
    }
    return enabledModules;
}

export function getXCoordinate(text: string, width: number, ctx: any) {
    return Math.round((width - ctx.measureText(text).width) / 2);
}

export function fillDoughnutChartText(textArray: any, chartText: any, ctx: any, width: any, height: any) {
    switch (textArray.length) {
        case 1:
            ctx.fillText(textArray[0], getXCoordinate(textArray[0], width, ctx), height / 2);
            break;
        case 2:
            ctx.fillText(textArray[0], getXCoordinate(textArray[0], width, ctx), height / 2.2);
            ctx.fillText(textArray[1], getXCoordinate(textArray[1], width, ctx), height / 1.8);
            break;
        case 3:
            ctx.fillText(textArray[0], getXCoordinate(textArray[0], width, ctx), height / 2.5);
            ctx.fillText(textArray[1], getXCoordinate(textArray[1], width, ctx), height / 1.9);
            ctx.fillText(textArray[2], getXCoordinate(textArray[2], width, ctx), height / 1.5);
            break;
        case 4:
            ctx.fillText(textArray[0], getXCoordinate(textArray[0], width, ctx), height / 3.0);
            ctx.fillText(textArray[1], getXCoordinate(textArray[1], width, ctx), height / 2.0);
            ctx.fillText(textArray[2], getXCoordinate(textArray[2], width, ctx), height / 1.5);
            ctx.fillText(textArray[3], getXCoordinate(textArray[3], width, ctx), height / 1.2);
            break;
        default:
            ctx.fillText(chartText, getXCoordinate(chartText, width, ctx), height / 2);
            break;
    }
}

let hubConnection: HubConnection | undefined;
export const getHubConnection = () => {
    if (hubConnection != null && hubConnection !== undefined) {
        return hubConnection;
    }

    const authToken = loadFromLocalStorage('AuthToken');
    if (authToken != null && authToken !== undefined) {
        hubConnection = new HubConnectionBuilder()
            .withUrl(`${import.meta.env.VITE_NOTIFICATION_HUB_URL}/hubs/alert?AuthToken=${authToken}`)
            .build();
        if (hubConnection != null && hubConnection !== undefined) {
            hubConnection
                .start()
                .then(() => { })
                .catch((_err: any) => console.log('Error while establishing connection :('));
            return hubConnection;
        }
    }
    return null;
}

export const resetHubConnection = () => {
    hubConnection = undefined;
}
export function getAuthorizationDoughnutData(array: any) {
    const result: any = [];
    array.reduce(function (res: any, value: any) {
        if (!res[value.twodproductname]) {
            res[value.twodproductname] = { twodproductname: value.twodproductname, numberOfAuthorizations: 0, productLine: value.productLine };
            result.push(res[value.twodproductname])
        }
        res[value.twodproductname].numberOfAuthorizations += value.numberOfAuthorizations;
        return res;
    }, {});
    return result;
}
export function base64ToBlob(base64: any, type: any = 'application/octet-stream') {
    const binStr = atob(base64);
    const len = binStr.length;
    const arr = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
        arr[i] = binStr.charCodeAt(i);
    }
    return new Blob([arr], { type: type });
}


export async function getFamilies() {
    if (productFamilies != undefined) {
        return productFamilies;
    }

    const queryString = `query{
    productfamilies {
        count,
        data{
        id,
    name,
    twodproductnames{
        id,
        name,
        twodproductmodels{
            id,
            name
        }
    }
        }
    }
}`
    const response: any = await sendRequestAndReceiveResponse(queryString, fetchFamiliesEndPoint);
    if (response !== undefined) {
        productFamilies = response.data.productfamilies;
    }

    return productFamilies;
}

export async function getHelpButtons() {
    if (helpButtons !== undefined) {
        return helpButtons;
    }
    const response = await sendRequestAndReceiveResponse(`query{
    gethelpbuttons{
        count,
        data{
       helpbuttonid,
description
        }
    }
}`, serviceManualEndPoint);
    helpButtons = response.data.gethelpbuttons.data;
    return helpButtons;
}


export async function getServiceManualTypes() {
    if (serviceManualTypes != undefined) {
        return serviceManualTypes;
    }
    const response = await sendRequestAndReceiveResponse(`query{
    getservicemanualtypes{
        count,
        data{
       id,
name
        }
    }
}`, serviceManualEndPoint);
    serviceManualTypes = response.data.getservicemanualtypes.data;
    return serviceManualTypes;
}

export async function getFirmTypes() {
    if (firmTypes != undefined) {
        return firmTypes;
    }
    const response = await sendRequestAndReceiveResponse(`query{
    getfirmtypes{
        count,
        data{
       id,
description
        }
    }
}`, firmTypeEndPoint);
    firmTypes = response.data.getfirmtypes.data;
    return firmTypes;
}

export const navigationButtonStyle = {
    backgroundColor: '#005f9e',
    borderRadius: 0,
    color: 'white',
    height: 30,
    marginLeft: 10
    ,
    '&:hover': {
        backgroundColor: '#009AD9'
    },
    '&:active': {
        backgroundColor: '#0D6AA8'
    },
    '&:disabled': {
        backgroundColor: '#8d8d91',
        color: '#fff'
    }
};

export const filterNames = (families: any, selectedFamilies: any) => {
    let productNames: any = [];
    families.map((x: any) => {

        if (selectedFamilies.indexOf(x.id) > -1) {
            x.twodproductnames.sort(function (a: any, b: any) {
                return a.name > b.name ? 1 : -1;
            });
            if (productNames.length == 0) {
                productNames = x.twodproductnames;
            }
            else {
                productNames = productNames.concat(x.twodproductnames);
            }
        }
    });
    return productNames;
};

export const filterModels = (productnames: any, selectedProductNames: any) => {
    let productModels: any = [];
    productnames.map((x: any) => {

        if (selectedProductNames.indexOf(x.id) > -1) {
            x.twodproductmodels.sort(function (a: any, b: any) {
                return a.name > b.name ? 1 : -1;
            });
            if (productModels.length == 0) {
                productModels = x.twodproductmodels;
            }
            else {
                productModels = productModels.concat(x.twodproductmodels);
            }
        }
    });
    return productModels;
}


export function clearvaluesonlogout() {

    serviceManualTypes = undefined;
    productFamilies = undefined;
    helpButtons = undefined;
}

